import type { AsyncThunk, AsyncThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { openBoard } from '@/features/board/slices/boardSlice'
import { openIntakes } from '@/features/intakes/slices/intakesSlice'
import { openMigration } from '@/features/migration/slices/migrationSlice'
import { openProject } from '@/features/projects/slices/projectDetailSlice'
import { fetchProjects } from '@/features/projects/slices/projectsSlice'
import { cacheKeyOf as requirementsCacheKey, openRequirements } from '@/features/requirements/slices/requirementsSlice'
import { fetchVersions } from '@/features/versions/slices/versionsSlice'
import type { AppDispatch, RootState } from '@/store'
import type { FetchCache } from '@/store/fetchCache'
import { startAppListening } from '@/store/listenerMiddleware'

/**
 * Cache follow-ups (TASK-3D6DB8B6, batch contract C-4).
 *
 * A realtime frame (or a post-mutation re-arm) that lands while a load for the
 * SAME key is in flight calls `markServerChanged`, which keeps the cache stale
 * through that load's success — but the panel's own re-dispatch was already
 * vetoed while the load was in flight. This listener closes the gap: when one
 * of the rows below settles successfully and its cache is still stale for the
 * key it just loaded (and, where given, the surface is still on screen), it
 * re-dispatches the SAME thunk with the SAME arg, exactly once. The thunk's
 * own `condition` still decides. A rejection never follows up (storm guard),
 * and a close reducer uses `invalidate`, which the settle clears — so closing
 * a panel never schedules a load. No I/O here: the thunk does the loading.
 */

/** One load that follows up a mid-flight server change. */
export interface CacheFollowUp<Returned, Arg, Rejected> {
  /** The guarded load thunk whose success is followed up. */
  thunk: AsyncThunk<Returned, Arg, { rejectValue: Rejected }>
  /** The slice's cache fields. */
  selectCache: (state: RootState) => FetchCache
  /** The cache key — byte-identical to the one the thunk's `condition` checks. */
  keyOf: (arg: Arg) => string
  /** Whether the surface that owns the load is still showing this arg. */
  isOnScreen?: (state: RootState, arg: Arg) => boolean
}

/** A registry row with its arg type erased, so the rows share one array. */
export interface RegisteredFollowUp {
  /** The load thunk, for identity checks. */
  thunk: { typePrefix: string }
  /** Dispatch the follow-up for `action` when it is this row's success and the cache asks for it. */
  followUp: (action: UnknownAction, state: RootState, dispatch: AppDispatch) => boolean
}

const followUpRow = <Returned, Arg, Rejected>(
  row: CacheFollowUp<Returned, Arg, Rejected>
): RegisteredFollowUp => ({
  thunk: row.thunk,
  followUp: (action, state, dispatch) => {
    if (!row.thunk.fulfilled.match(action)) return false
    const arg = action.meta.arg
    const cache = row.selectCache(state)
    if (cache.loading || !cache.invalidated || cache.loadedKey !== row.keyOf(arg)) return false
    if (row.isOnScreen !== undefined && !row.isOnScreen(state, arg)) return false
    // The generic `Arg` defeats AsyncThunk's conditional call signature; the
    // concrete thunks in the registry below all take a required arg.
    const load = row.thunk as (arg: Arg) => AsyncThunkAction<Returned, Arg, { rejectValue: Rejected }>
    void dispatch(load(arg))
    return true
  }
})

/**
 * The loads that receive `markServerChanged`: the five Tier-1 rows (C-3, C-4)
 * plus the project row and the portfolio list, which id-only `project.updated`
 * frames mark stale since TASK-7922504E fix cycle 1.
 */
export const cacheFollowUps: readonly RegisteredFollowUp[] = [
  followUpRow({
    thunk: openBoard,
    selectCache: (state) => state.board,
    keyOf: (arg) => arg.projectId,
    isOnScreen: (state, arg) => state.board.projectId === arg.projectId
  }),
  followUpRow({
    thunk: fetchVersions,
    selectCache: (state) => state.projectVersions,
    keyOf: (arg) => arg.projectId
  }),
  followUpRow({
    thunk: openIntakes,
    selectCache: (state) => state.intakes,
    keyOf: (arg) => `${arg.projectId}:${arg.versionId}`,
    isOnScreen: (state, arg) =>
      state.intakes.projectId === arg.projectId && state.intakes.activeVersionId === arg.versionId
  }),
  followUpRow({
    thunk: openRequirements,
    selectCache: (state) => state.requirements,
    keyOf: (arg) => requirementsCacheKey(arg.projectId, arg.filters),
    isOnScreen: (state, arg) => state.requirements.projectId === arg.projectId
  }),
  followUpRow({
    thunk: openMigration,
    selectCache: (state) => state.migration,
    keyOf: (arg) => arg.projectId,
    isOnScreen: (state, arg) => state.migration.projectId === arg.projectId
  }),
  followUpRow({
    thunk: openProject,
    selectCache: (state) => state.projectDetail,
    keyOf: (arg) => arg.projectId,
    isOnScreen: (state, arg) => state.projectDetail.projectId === arg.projectId
  }),
  followUpRow({
    thunk: fetchProjects,
    selectCache: (state) => state.projects,
    keyOf: (orgId) => orgId,
    isOnScreen: (state, orgId) => state.session.activeOrgId === orgId
  })
]

const isFollowedUpSuccess = (action: UnknownAction): boolean =>
  cacheFollowUps.some((row) => action.type === `${row.thunk.typePrefix}/fulfilled`)

const followUpEffect = (
  action: UnknownAction,
  listenerApi: { getState: () => RootState; dispatch: AppDispatch }
): void => {
  const state = listenerApi.getState()
  for (const row of cacheFollowUps) {
    if (row.followUp(action, state, listenerApi.dispatch)) return
  }
}

/**
 * Register the listener on the shared middleware. Idempotent: RTK keeps one
 * entry per identical (predicate, effect) pair, and both are module constants.
 */
export const registerCacheFollowUps = (): void => {
  startAppListening({ predicate: isFollowedUpSuccess, effect: followUpEffect })
}

registerCacheFollowUps()
